import {
  FOOD_MAX_KCAL_PER_100G,
  type AdminFoodRequestDecisions,
  type AdminFoodRequestDetail,
  type AdminFoodRequestField,
  type FoodMacrosView,
  type FoodResearchFieldName,
  type PortionUnitName,
} from '@steado/admin-contract/foods';

import type { ConfidenceLevel } from './request-copy';

/**
 * A new food's review: the fields the reviewer must decide, each with the
 * values on offer, and the approval those choices make. Nothing is chosen
 * for the reviewer: a field the research left below high confidence, or did
 * not find at all, waits for a pick.
 */

export type OptionId = 'ai' | 'estimate' | 'own' | 'none';

export interface NutritionDraft {
  readonly kcal: string;
  readonly proteinG: string;
  readonly carbsG: string;
  readonly fatG: string;
  readonly fibreG: string;
}

export interface PortionDraft {
  readonly label: string;
  readonly unit: PortionUnitName;
  readonly grams: string;
}

export interface FlagChoice {
  readonly option: OptionId;
  readonly nutrition?: NutritionDraft;
  readonly portion?: PortionDraft;
}

export type FlagChoices = Readonly<Partial<Record<FoodResearchFieldName, FlagChoice>>>;

export interface FlagOption {
  readonly id: OptionId;
  /** Where the value comes from, under it. */
  readonly source: string;
}

export interface NewFoodFlag {
  readonly field: FoodResearchFieldName;
  readonly label: string;
  readonly confidence: ConfidenceLevel;
  readonly note: string | null;
  readonly sources: readonly number[];
  readonly options: readonly FlagOption[];
}

export const EMPTY_NUTRITION: NutritionDraft = { kcal: '', proteinG: '', carbsG: '', fatG: '', fibreG: '' };

const FLAG_LABELS: Readonly<Record<FoodResearchFieldName, string>> = {
  nutrition: 'Energy and macros per 100 g',
  portions: 'Default portion',
  recipe: 'Recipe',
};

/** The fields this new food waits on: flagged by the draft, or missing from it. */
export function newFoodFlags(detail: AdminFoodRequestDetail): NewFoodFlag[] {
  const draft = detail.draft;
  if (draft === null) return [];
  const flagged = new Map<FoodResearchFieldName, AdminFoodRequestField | undefined>(
    draft.fields.filter((field) => field.needsDecision).map((field) => [field.field, field]),
  );
  if (draft.proposed?.macrosPer100g === null || draft.proposed === null) {
    if (!flagged.has('nutrition')) flagged.set('nutrition', undefined);
  }
  if ((draft.proposed?.portions.length ?? 0) === 0 && !flagged.has('portions'))
    flagged.set('portions', undefined);

  const estimate = draft.current;
  const order: FoodResearchFieldName[] = ['nutrition', 'portions', 'recipe'];
  return order.flatMap((name) => {
    if (!flagged.has(name)) return [];
    const field = flagged.get(name);
    const options: FlagOption[] = [];
    if (name === 'nutrition') {
      if (draft.proposed?.macrosPer100g != null) options.push({ id: 'ai', source: 'AI’s pick' });
      if (estimate?.macrosPer100g != null)
        options.push({ id: 'estimate', source: 'The estimate it was logged as' });
      options.push({ id: 'own', source: 'Typed by you' });
    } else if (name === 'portions') {
      if ((draft.proposed?.portions.length ?? 0) > 0) options.push({ id: 'ai', source: 'AI’s pick' });
      if ((estimate?.portions.length ?? 0) > 0)
        options.push({ id: 'estimate', source: 'The estimate’s portion' });
      options.push({ id: 'own', source: 'Typed by you' });
    } else {
      if ((draft.food?.recipeLines ?? []).every((line) => line.ingredientFoodId !== null))
        options.push({ id: 'ai', source: 'AI’s pick' });
      options.push({
        id: 'none',
        source: 'Its diet and allergens stay strictest until a recipe is reviewed',
      });
    }
    return [
      {
        field: name,
        label: FLAG_LABELS[name],
        confidence: field?.confidence ?? 'none',
        note:
          field?.note ??
          (name === 'nutrition'
            ? 'The research found no figures its sources support.'
            : name === 'portions'
              ? 'The research found no portion weights its sources support.'
              : null),
        sources: field?.sources ?? [],
        options,
      },
    ];
  });
}

function number(text: string): number | null {
  if (text.trim() === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** Your own figures, when every one is in range; fibre may be left empty, which is not zero. */
export function ownMacros(draft: NutritionDraft | undefined): FoodMacrosView | null {
  if (draft === undefined) return null;
  const kcal = number(draft.kcal);
  const proteinG = number(draft.proteinG);
  const carbsG = number(draft.carbsG);
  const fatG = number(draft.fatG);
  const fibreG = number(draft.fibreG);
  const grams = [proteinG, carbsG, fatG];
  if (kcal === null || kcal < 0 || kcal > FOOD_MAX_KCAL_PER_100G) return null;
  if (grams.some((value) => value === null || value < 0 || value > 100)) return null;
  if (draft.fibreG.trim() !== '' && (fibreG === null || fibreG < 0 || fibreG > 100)) return null;
  return { kcal, proteinG: proteinG ?? 0, carbsG: carbsG ?? 0, fatG: fatG ?? 0, fibreG };
}

export function ownPortion(draft: PortionDraft | undefined) {
  if (draft === undefined) return null;
  const grams = number(draft.grams);
  const label = draft.label.trim();
  if (label === '' || /^[0-9]/.test(label) || grams === null || grams <= 0 || grams > 5000) return null;
  return { label, unit: draft.unit, grams, isDefault: true };
}

export function isDecided(flag: NewFoodFlag, choice: FlagChoice | undefined): boolean {
  if (choice === undefined) return false;
  if (choice.option !== 'own') return true;
  return flag.field === 'nutrition'
    ? ownMacros(choice.nutrition) !== null
    : ownPortion(choice.portion) !== null;
}

/** The figures per 100 g the new food would have, as chosen so far; null while undecided. */
export function chosenMacros(
  detail: AdminFoodRequestDetail,
  flags: readonly NewFoodFlag[],
  choices: FlagChoices,
): FoodMacrosView | null {
  const proposed = detail.draft?.proposed?.macrosPer100g ?? null;
  if (!flags.some((flag) => flag.field === 'nutrition')) return proposed;
  const choice = choices.nutrition;
  switch (choice?.option) {
    case 'ai':
      return proposed;
    case 'estimate':
      return detail.draft?.current?.macrosPer100g ?? null;
    case 'own':
      return ownMacros(choice.nutrition);
    default:
      return null;
  }
}

/** Every value energy could take from the options on offer, for "226 to 237 kcal" before a pick. */
export function kcalOptions(detail: AdminFoodRequestDetail): number[] {
  const values = [detail.draft?.proposed?.macrosPer100g?.kcal, detail.draft?.current?.macrosPer100g?.kcal];
  return values.filter((value): value is number => value !== undefined);
}

/** The approval: a decision for every flag, nothing for the fields the research carried well. */
export function newFoodDecisions(
  detail: AdminFoodRequestDetail,
  flags: readonly NewFoodFlag[],
  choices: FlagChoices,
): AdminFoodRequestDecisions {
  const draft = detail.draft;
  const decisions: AdminFoodRequestDecisions = {};
  for (const flag of flags) {
    const choice = choices[flag.field];
    if (choice === undefined) continue;
    if (flag.field === 'nutrition') {
      const proposed = draft?.proposed?.macrosPer100g ?? null;
      if (choice.option === 'ai' && proposed !== null) decisions.nutrition = { value: proposed };
      if (choice.option === 'estimate') decisions.nutrition = { keep: true };
      const own = choice.option === 'own' ? ownMacros(choice.nutrition) : null;
      if (own !== null) decisions.nutrition = { value: own };
    } else if (flag.field === 'portions') {
      const proposed = draft?.proposed?.portions ?? [];
      if (choice.option === 'ai' && proposed.length > 0) {
        const hasDefault = proposed.some((portion) => portion.isDefault);
        decisions.portions = {
          value: proposed.map((portion, index) => ({
            ...portion,
            isDefault: hasDefault ? portion.isDefault : index === 0,
          })),
        };
      }
      if (choice.option === 'estimate') decisions.portions = { keep: true };
      const own = choice.option === 'own' ? ownPortion(choice.portion) : null;
      if (own !== null) decisions.portions = { value: [own] };
    } else {
      const lines = draft?.food?.recipeLines ?? [];
      if (choice.option === 'ai' && lines.length > 0) {
        decisions.recipe = {
          value: lines.map((line) => ({
            rawName: line.rawName,
            ingredientFoodId: line.ingredientFoodId,
            grams: line.grams,
            retentionGroup: line.retentionGroup,
          })),
        };
      }
      if (choice.option === 'none') decisions.recipe = { keep: true };
    }
  }
  return decisions;
}
